import React, { useState, useEffect } from 'react'
import { useStyletron } from 'styletron-react'
import { lightTheme } from '@/customTheme'
import { useTranslation } from 'react-i18next'

interface AnimatedPlaceholderProps {
  visible: boolean
  placeholders: string[]
  disabled?: boolean
  hasValue?: boolean
  isFocused?: boolean
}

const AnimatedPlaceholder: React.FC<AnimatedPlaceholderProps> = ({ placeholders, disabled, visible, hasValue, isFocused }) => {
  const [css] = useStyletron()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const cycleDoneRef = React.useRef(false)
  const currentIndexRef = React.useRef(0)
  const animationDuration = 300
  const cycleInterval = 3300
  const scheduleRef = React.useRef<number | null>(null)
  const enterTimeoutRef = React.useRef<number | null>(null)

  // duplicate first placeholder at end if multiple to create seamless loop
  const effectivePlaceholders =
    placeholders.length > 1 ? [...placeholders, placeholders[0]] : placeholders

  // Keep a ref of the latest index for scheduling decisions
  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])

  // Reset to default when input has value
  useEffect(() => {
    if (hasValue || isFocused) {
      // Clear all timers
      if (scheduleRef.current) { clearTimeout(scheduleRef.current); scheduleRef.current = null }
      if (enterTimeoutRef.current) { clearTimeout(enterTimeoutRef.current); enterTimeoutRef.current = null }
      
      // Reset to default placeholder
      setCurrentIndex(0)
      setIsTransitioning(false)
      setPreviousIndex(null)
      cycleDoneRef.current = true
    }
  }, [hasValue, isFocused])

  // Reset cycle when textarea is blurred and empty
  useEffect(() => {
    if (!isFocused && !hasValue && !disabled) {
      cycleDoneRef.current = false
    }
  }, [isFocused, hasValue, disabled])

  // Handle initial mount enter animation and reset when disabled toggles
  useEffect(() => {
    // Cleanup any scheduled timers on changes
    if (scheduleRef.current) { clearTimeout(scheduleRef.current); scheduleRef.current = null }
    if (enterTimeoutRef.current) { clearTimeout(enterTimeoutRef.current); enterTimeoutRef.current = null }

    if (disabled) {
      setCurrentIndex(0)
      setIsTransitioning(false)
      setPreviousIndex(null)
      cycleDoneRef.current = false
      return
    }

    // On mount or re-enable: show first without any enter animation
    setIsTransitioning(false)

    return () => {
      if (scheduleRef.current) clearTimeout(scheduleRef.current)
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current)
    }
  }, [disabled, animationDuration])

  // Schedule a single full cycle of placeholder changes
  useEffect(() => {
    if (disabled || effectivePlaceholders.length <= 1 || hasValue || isFocused) return

    const scheduleNext = () => {
      if (cycleDoneRef.current) return

      scheduleRef.current = window.setTimeout(() => {
        const isLast = currentIndexRef.current === effectivePlaceholders.length - 1
        if (isLast) {
          // Jump to first without any exit/enter animations
          setCurrentIndex(0)
          setIsTransitioning(false)
          setPreviousIndex(null)
          cycleDoneRef.current = true
          return
        }

        // Start transition
        setIsTransitioning(true)
        setPreviousIndex(currentIndexRef.current)
        setCurrentIndex(prev => (prev < effectivePlaceholders.length - 1 ? prev + 1 : 0))

        enterTimeoutRef.current = window.setTimeout(() => {
          setIsTransitioning(false)
          setPreviousIndex(null)
          // If cycle not done, schedule another
          if (!cycleDoneRef.current) {
            scheduleNext()
          }
        }, animationDuration)
      }, cycleInterval)
    }

    scheduleNext()

    return () => {
      if (scheduleRef.current) clearTimeout(scheduleRef.current)
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current)
    }
  }, [effectivePlaceholders.length, disabled, cycleInterval, animationDuration, hasValue, isFocused])

  const [t] = useTranslation()

  return (
    <div
      className={css({
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        height: '20px',
        pointerEvents: 'none',
        zIndex: visible ? 1 : -1,
        overflow: 'hidden',
      })}
    >
      <TextAnimation
        key={`current-${currentIndex}`}
        placeholder={effectivePlaceholders[currentIndex]}
        disabled={disabled}
        animDurationMs={animationDuration}
        phase={isTransitioning ? 'enter' : 'idle'}
        visible={visible}
      />
      {isTransitioning && previousIndex !== null && (
        <TextAnimation
          key={`previous-${previousIndex}`}
          placeholder={effectivePlaceholders[previousIndex]}
          disabled={disabled}
          animDurationMs={animationDuration}
          phase={'exit'}
          visible={visible}
        />
      )}
    </div>
  )
}

export default AnimatedPlaceholder


function TextAnimation({ placeholder, disabled, animDurationMs = 300, phase, visible = false}: { placeholder: string; disabled?: boolean; animDurationMs?: number; phase: 'enter' | 'idle' | 'exit', visible?: boolean}) {
  const [css] = useStyletron()
  const [t] = useTranslation()
  const yOffsetPx = 30

  const baseStyle = {
    lineHeight: '20px',
    fontSize: '14px',
    fontWeight: 400,
    color: disabled ? lightTheme.colors.text.text_200_disable : lightTheme.colors.text.text_600_default,
    willChange: 'transform' as const,
  }

  const animStyle =
    phase === 'enter'
      ? {
          animationName: {
            from: { transform: `translateY(${yOffsetPx}px)` },
            to: { transform: 'translateY(0px)' },
          } as any,
          animationDuration: `${animDurationMs}ms`,
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards',
        }
      : phase === 'exit'
      ? {
          animationName: {
            from: { transform: 'translateY(0px)' },
            to: { transform: `translateY(-${yOffsetPx}px)` },
          } as any,
          animationDuration: `${animDurationMs}ms`,
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards',
        }
      : {}

  return (
    <div
      className={css({
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        height: '20px',
        pointerEvents: 'none',
        zIndex: visible ? 1 : -1,
        overflow: 'hidden',
      })}
    >
      <span className={css({ ...baseStyle, ...animStyle })}>{t(placeholder)}</span>
    </div>
  )
}
